import {GroupBase} from "react-select";

import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {
  MetricDataTypeDef,
  MetricDefinition,
  MetricDimensionDef,
  MetricsExplorerSchema,
} from "@/lib/types/metricsExplorer.ts";

/** Dimensions every table carries; not useful to group by, or internal to the store. */
const HIDDEN_DIMENSIONS = ["check_id", "check_date", "check_time"];

export const findCheckType = (schema: MetricsExplorerSchema | undefined, checkType: string): MetricDataTypeDef[] => {
  const entry = schema?.check_types.find((item) => item.check_type === checkType);
  return entry?.data_types ?? [];
};

/**
 * Data types queryable for every one of the selected check types.
 *
 * An intersection rather than a union: a series is one query against one table, so a data
 * type only half the selected checks write to would silently return partial data.
 */
export const availableDataTypes = (
  schema: MetricsExplorerSchema | undefined,
  checkTypes: string[]
): MetricDataTypeDef[] => {
  if (!schema || checkTypes.length === 0) {
    return [];
  }
  const [first, ...rest] = checkTypes;
  const candidates = findCheckType(schema, first);
  return candidates.filter((dataType) =>
    rest.every((checkType) => findCheckType(schema, checkType).some((other) => other.name === dataType.name))
  );
};

export const findDataType = (
  schema: MetricsExplorerSchema | undefined,
  checkTypes: string[],
  dataTypeName: string
): MetricDataTypeDef | undefined =>
  availableDataTypes(schema, checkTypes).find((dataType) => dataType.name === dataTypeName);

/** Only numeric metrics can be aggregated, and the explorer only draws aggregates. */
export const availableMetrics = (dataType: MetricDataTypeDef | undefined): MetricDefinition[] =>
  (dataType?.metrics ?? []).filter((metric) => metric.numeric);

export const availableDimensions = (dataType: MetricDataTypeDef | undefined): MetricDimensionDef[] =>
  (dataType?.dimensions ?? []).filter((dimension) => !HIDDEN_DIMENSIONS.includes(dimension.name));

/**
 * Narrow a series' selections to what the current data type still offers.
 *
 * Called when the checks or data type change: metrics, group-by dimensions and filters
 * chosen for the previous table would otherwise linger and produce a request the backend
 * rejects.
 */
export const pruneSelections = (
  dataType: MetricDataTypeDef | undefined,
  metrics: string[],
  groupBy: string[],
  filters: Record<string, string[]>
): {metrics: string[]; groupBy: string[]; filters: Record<string, string[]>} => {
  const metricNames = new Set(availableMetrics(dataType).map((metric) => metric.name));
  const dimensionNames = new Set(availableDimensions(dataType).map((dimension) => dimension.name));
  return {
    metrics: metrics.filter((metric) => metricNames.has(metric)),
    groupBy: groupBy.filter((dimension) => dimensionNames.has(dimension)),
    filters: Object.fromEntries(Object.entries(filters).filter(([dimension]) => dimensionNames.has(dimension))),
  };
};

export const uniqueCheckTypes = (types: string[]): string[] => Array.from(new Set(types)).sort();

/** How a check type fits the ones already picked: 0 already picked, 1 shares data, 2 no fit. */
const checkTypeFit = (schema: MetricsExplorerSchema | undefined, selectedTypes: string[], checkType: string) => {
  if (selectedTypes.length === 0 || selectedTypes.includes(checkType)) {
    return 0;
  }
  // Without the catalog the fit is unknown, and claiming "nothing in common" would be wrong.
  if (!schema) {
    return 1;
  }
  return availableDataTypes(schema, [...selectedTypes, checkType]).length > 0 ? 1 : 2;
};

/**
 * Check search results in sections by check type, ordered by how well each fits the checks
 * already in the series: their own types, then types sharing a data type with them, then
 * the rest, labelled as such. A series is one query against one table, so a mix with no
 * data type in common can only end in an error; this steers away from it before it happens.
 * Within a section the server's order is kept.
 */
export const groupChecksByType = (
  checks: CheckInfo[],
  schema: MetricsExplorerSchema | undefined,
  selectedTypes: string[]
): GroupBase<CheckInfo>[] => {
  const byType = new Map<string, CheckInfo[]>();
  checks.forEach((check) => {
    const key = check.monitoring_service_type ?? check.type;
    byType.set(key, [...(byType.get(key) ?? []), check]);
  });
  return [...byType.entries()]
    .map(([checkType, options]) => ({fit: checkTypeFit(schema, selectedTypes, checkType), options}))
    .sort((a, b) => a.fit - b.fit)
    .map(({fit, options}) => ({
      label: fit === 2 ? `${options[0].type} · no metrics in common` : options[0].type,
      options,
    }));
};
